/* eslint-disable max-lines */
import Button from '@components/atoms/ui-kit/button.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import { MultiValue } from 'react-select';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslateDocument } from '@utils/api/documents/use-translate-document.tsx';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { css, StyleSheet } from 'aphrodite';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import cn from 'classnames';
import { DocumentTranslationLanguage, Option } from '@utils/types/shared.ts';
import { getDocumentRaw } from '@utils/api/documents/use-get-document-raw.tsx';
import {
  getDocumentDirectUrl,
  triggerDownloadFromUrl
} from '@utils/api/documents/use-get-document-direct.tsx';
import { parse } from '@utils/extensions/content-disposition.extension.ts';
import saveAs from 'file-saver';
import {
  useShareDocument,
  useShareDocumentEmail
} from '@utils/api/magic-links/use-post-share-link.tsx';
import { useParams } from 'react-router-dom';
import { useCaption } from '@utils/providers/caption-context.tsx';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import { formatOptions } from '@utils/constants/download-options.ts';
import {
  ClipboardCopy,
  Languages,
  Upload,
  WandSparkles,
  Mail,
  FileText,
  FileCode,
  FileVideo,
  Subtitles,
  Loader2
} from 'lucide-react';
import Checkbox from '@components/atoms/ui-kit/checkbox.tsx';
import { useEntitlements } from '@utils/providers/entitlements-context.tsx';
import { useOrganization } from '@utils/api/users/use-get-organization.tsx';
import { useGetGlossaries } from '@utils/api/glossaries/use-get-glossaries.tsx';
import { Glossary } from '@utils/types/glossary.ts';
import { useStartBurnInExport } from '@utils/api/documents/use-start-burn-in-export.tsx';
import { useBurnInJobStatus } from '@utils/api/documents/use-burn-in-job-status.ts';
import PublishAction from '@components/publishing/publish-action.tsx';

/* eslint-disable no-restricted-syntax -- Semantic caption editor colors (subtitle palette, playback cues, pill foregrounds on tinted backgrounds) must stay stable across themes */

type Params = {
  sessionId: string;
  documentId: string;
};

// Same helpers as transcript editor
function parseEmails(value: string): string[] {
  return value
    .split(/[\s,;\n\r]+/)
    .map(e => e.trim())
    .filter(Boolean);
}

function isValidEmail(email: string): boolean {
  // Simple but good-enough email pattern
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// One id for every toast the burn-in flow raises. react-hot-toast replaces a
// toast that reuses an id, so a repeated start can only ever update the toast
// already on screen instead of stacking a second identical one.
const BURN_IN_TOAST_ID = 'burn-in-export';

interface CaptionExportDocumentProps {
  /** Controlled download-modal state. Supplied when the Export action
   *  is also exposed in the editor header's overflow menu (narrow
   *  widths, #2231) so the modal can open from outside the component's
   *  own button. Falls back to internal state when omitted. */
  downloadOpen?: boolean;
  onDownloadOpenChange?: (open: boolean) => void;
  /** Controlled publish-dialog state, same pattern as `downloadOpen` so
   *  the editor header's overflow menu can open Publish at phone widths
   *  where the inline button is hidden. */
  publishOpen?: boolean;
  onPublishOpenChange?: (open: boolean) => void;
}

export const CaptionExportDocument = ({
  downloadOpen,
  onDownloadOpenChange,
  publishOpen,
  onPublishOpenChange
}: CaptionExportDocumentProps = {}) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const { hasFeature } = useEntitlements();

  const { sessionId, documentId } = useParams<Params>();
  const { session, documentResult } = useCaption();

  const [internalModal, setInternalModal] = useState<
    '' | 'download' | 'translate'
  >('');
  // Download is the one modal the parent may want to force open from
  // the overflow menu; translate stays local. When `downloadOpen` is
  // supplied, it takes precedence over the internal state for that
  // specific modal id.
  const modal =
    downloadOpen === true
      ? 'download'
      : downloadOpen === false && internalModal === 'download'
        ? ''
        : internalModal;
  const setModal = (next: '' | 'download' | 'translate') => {
    if (onDownloadOpenChange && (next === 'download' || next === '')) {
      // Reflect to the parent so an external action can read the
      // current state. Internal state still mirrors so `translate`
      // doesn't need parent cooperation.
      onDownloadOpenChange(next === 'download');
    }
    setInternalModal(next);
  };
  const [linkModal, setLinkModal] = useState<boolean>(false);
  const [link, setLink] = useState<string>('');
  const [filenameInput, setFilenameInput] = useState(session?.filename || '');
  const [selectedLanguages, setSelectedLanguages] = useState<Option[]>([]);
  const { mutate: translateDoc, isLoading: translationIsLoading } =
    useTranslateDocument();
  const { mutate: translateAll, isLoading: translateAllLoading } =
    useTranslateDocument();
  const { data: organization } = useOrganization();
  const [includeSpeakers, setIncludeSpeakers] = useState(true);
  const [includeTimestamps] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);

  // Burn-in video state
  const [, setBurnInJobId] = useState<number | null>(null);
  const [burnInStatus, setBurnInStatus] = useState<string | null>(null);
  const [burnInBannerDismissed, setBurnInBannerDismissed] = useState(false);
  const { mutate: startBurnIn, isLoading: burnInStarting } =
    useStartBurnInExport();
  const { data: burnInJob } = useBurnInJobStatus({
    sessionId: sessionId || '',
    documentId: documentId || '',
    enabled: burnInStatus === 'queued' || burnInStatus === 'processing'
  });

  const burnInInProgress =
    burnInStatus === 'queued' || burnInStatus === 'processing';
  const showBurnInBanner = burnInInProgress && !burnInBannerDismissed;

  // Email share state (mirror TranscriptExportDocument)
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [lastEmailsSentCount, setLastEmailsSentCount] = useState<number | null>(
    null
  );

  const parsedEmails = useMemo(() => parseEmails(emailInput), [emailInput]);

  // Glossary state
  const [selectedGlossaryId, setSelectedGlossaryId] = useState<number | null>(
    null
  );
  const { data: glossariesData } = useGetGlossaries();

  const matchingGlossaries = useMemo(() => {
    if (!glossariesData?.result || selectedLanguages.length === 0) return [];
    const targetCodes = selectedLanguages.map(l => l.value.toUpperCase());
    return glossariesData.result.filter(
      (g: Glossary) =>
        g.is_active && targetCodes.includes(g.target_language.toUpperCase())
    );
  }, [glossariesData, selectedLanguages]);

  const MAX_MESSAGE_LENGTH = 500;
  const remainingChars = Math.max(0, MAX_MESSAGE_LENGTH - message.length);

  // Handle burn-in job status updates
  useEffect(() => {
    if (!burnInJob) return;
    setBurnInStatus(burnInJob.status);

    if (burnInJob.status === 'ready' && burnInJob.download_url) {
      const filename = session?.filename
        ? `${session.filename.replace(/\.[^.]+$/, '')}-burned.mp4`
        : 'burned-captions.mp4';
      triggerDownloadFromUrl(burnInJob.download_url, filename, 'video/mp4')
        .then(() => {
          toast.success(t('download-ready'), { id: BURN_IN_TOAST_ID });
          setBurnInJobId(null);
          setBurnInStatus(null);
        })
        .catch(() => {
          toast.error(t('error-downloading-document'), {
            id: BURN_IN_TOAST_ID
          });
          setBurnInJobId(null);
          setBurnInStatus(null);
        });
    } else if (burnInJob.status === 'failed') {
      toast.error(burnInJob.error || t('error-downloading-document'), {
        id: BURN_IN_TOAST_ID
      });
      setBurnInJobId(null);
      setBurnInStatus(null);
    }
  }, [burnInJob, session?.filename, t]);

  const handleBurnInExport = useCallback(() => {
    if (!sessionId || !documentId) return;
    // A second start while one is in flight produced a second identical
    // error toast, since each rejection raised its own.
    if (burnInStarting) return;
    setBurnInBannerDismissed(false);
    startBurnIn(
      { sessionId, documentId },
      {
        onSuccess: data => {
          setBurnInJobId(data.job_id);
          setBurnInStatus(data.status);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          if (
            error?.response?.status === 409 &&
            error?.response?.data?.error === 'job-already-running'
          ) {
            setBurnInStatus('queued');
            return;
          }
          const msg =
            error?.response?.data?.error_description ||
            error?.response?.data?.detail ||
            t('error-downloading-document');
          toast.error(msg, { id: BURN_IN_TOAST_ID });
        }
      }
    );
  }, [sessionId, documentId, startBurnIn, burnInStarting, t]);

  const styles = useMemo(
    () =>
      StyleSheet.create({
        topNavContainer: {
          borderBottom: `1px solid ${getColour('border')}`
        },
        topNavContainerColor: {
          backgroundColor: getColour('secondLayer')
        },
        iconsColor: {
          color: getColour('icon')
        },
        userText: {
          color: getColour('normalText')
        }
      }),
    [getColour]
  );

  const { mutate: shareDocument } = useShareDocument({
    onSuccess: data => {
      if (data && data.result.link) {
        setLinkModal(true);
        setLink(data.result.link);
        setGeneratingLink(false);
      } else {
        toast.error(t('failed-to-generate-link'));
        setGeneratingLink(false);
      }
    },
    onError: () => {
      toast.error(t('failed-to-generate-link'));
      setGeneratingLink(false);
    }
  });

  const { mutateAsync: sendMagicLinkEmail, isLoading: isSendingEmail } =
    useShareDocumentEmail();

  const handleExport = async (format: string) => {
    if (!documentResult) {
      toast.error(t('error-downloading-document'));
      return;
    }

    toast.loading(t('preparing-download'), { id: 'exporting-document' });

    try {
      if (format === 'mp4-burnt') {
        toast.dismiss('exporting-document');
        toast.dismiss(BURN_IN_TOAST_ID);
        handleBurnInExport();
        setModal('');
        return;
      } else if (format === 'media') {
        await handleMediaDownload();
        setModal('');
        toast.success(t('download-ready'), { id: 'exporting-document' });
      } else {
        // Formats that typically have pre-generated files and support direct download
        const supportsDirectDownload = ['docx', 'pdf'].includes(format);

        if (supportsDirectDownload) {
          try {
            const response = await getDocumentDirectUrl({
              sessionId: session?.session_id as string,
              documentId: documentResult.result.id as string,
              format,
              without_timestamps: !includeTimestamps,
              without_speakers: !includeSpeakers
            });

            // Trigger download directly from Azure Blobs (bypasses load balancer)
            await triggerDownloadFromUrl(
              response.result.download_url,
              response.result.filename,
              response.result.content_type
            );
            setModal('');
            toast.success(t('download-ready'), { id: 'exporting-document' });
            return; // Success, exit early
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } catch (directError: any) {
            // If direct download not supported, fall through to streaming method
            if (
              directError?.response?.status !== 400 ||
              directError?.response?.data?.error !==
                'direct-download-not-supported'
            ) {
              // Re-throw non-fallback errors
              throw directError;
            }
            // Otherwise continue to streaming fallback below
          }
        }

        // Use streaming method for formats without pre-generated files
        // or as fallback when direct download not supported
        const response = await getDocumentRaw({
          sessionId: session?.session_id as string,
          documentId: documentResult.result.id as string,
          format,
          without_timestamps: !includeTimestamps,
          without_speakers: !includeSpeakers
        });

        const disposition = parse(
          response.headers['content-disposition'] || ''
        );
        const filename =
          disposition?.parameters?.filename || `download.${format}`;
        saveAs(response.data, filename);
        setModal('');
        toast.success(t('download-ready'), { id: 'exporting-document' });
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast.dismiss('exporting-document');
      if (error?.response?.status === 404) {
        toast.error(t('document-not-found'));
      } else {
        toast.error(t('error-downloading-document'));
      }
    }
  };

  const handleMediaDownload = async () => {
    if (!session?.media_url) {
      toast.error(t('error-downloading-media'));
      return;
    }
    // Fetch the media into a blob and download programmatically. A bare
    // cross-origin <a download href> is ignored by Safari (and others) for
    // cross-origin URLs, so it opens an inline player instead of downloading.
    try {
      await triggerDownloadFromUrl(
        session.media_url,
        session.filename || 'media-file'
      );
    } catch {
      toast.error(t('error-downloading-media'));
    }
  };

  const options: Option[] = useMemo(
    () =>
      Object.keys(DocumentTranslationLanguage).map(
        key =>
          ({
            label: key,
            value:
              DocumentTranslationLanguage[
                key as keyof typeof DocumentTranslationLanguage
              ]
          }) as Option
      ),
    []
  );

  const handleTranslate = () => {
    if (!documentResult) return;

    const toastId = toast.loading(t('translating-document'));

    translateDoc(
      {
        sessionId: session?.session_id as string,
        documentId: documentResult?.result.id as string,
        languages: selectedLanguages.map(lang => lang.value.toUpperCase()),
        filename: filenameInput,
        glossary_id: selectedGlossaryId
      },
      {
        onSuccess: async data => {
          toast.dismiss(toastId);
          setModal('');

          const shouldAutoDownload =
            (documentResult?.result.use_plain_document &&
              !documentResult?.result.plain_document_changed) ||
            false;

          if (shouldAutoDownload && data?.json && Array.isArray(data.json)) {
            for (const doc of data.json) {
              if (!doc.id || !session?.session_id) continue;
              try {
                const resp = await getDocumentDirectUrl({
                  sessionId: session.session_id,
                  documentId: doc.id,
                  format: 'docx'
                });
                await triggerDownloadFromUrl(
                  resp.result.download_url,
                  resp.result.filename,
                  resp.result.content_type
                );
              } catch {
                try {
                  const resp = await getDocumentRaw({
                    sessionId: session.session_id,
                    documentId: doc.id,
                    format: 'docx'
                  });
                  const disposition = parse(
                    resp.headers['content-disposition'] || ''
                  );
                  const fname =
                    disposition?.parameters?.filename ||
                    doc.filename ||
                    'translated.docx';
                  saveAs(resp.data, fname);
                } catch {
                  toast.error(t('error-downloading-document'));
                }
              }
            }
          }
        },
        onError: () => {
          toast.dismiss(toastId);
        }
      }
    );
  };

  const orgDefaultLanguages = organization?.default_translation_languages ?? [];

  const handleTranslateAll = () => {
    if (!documentResult || orgDefaultLanguages.length === 0) return;

    toast(
      t('translating-to-n-languages', {
        count: orgDefaultLanguages.length
      })
    );

    translateAll(
      {
        sessionId: session?.session_id as string,
        documentId: documentResult.result.id as string,
        languages: orgDefaultLanguages.map(l => l.toUpperCase()),
        filename: session?.filename
      },
      {
        onSuccess: () => {
          toast.success(t('translate-all-completed'));
        }
      }
    );
  };

  const handleGenerateLink = async () => {
    if (!sessionId || !documentId) return;

    setGeneratingLink(true);
    shareDocument({ sessionId, documentId });
  };

  const handleCopyLink = () => {
    navigator.clipboard
      .writeText(link)
      .then(() => {
        toast.success(t('copied-to-clipboard') || 'Link copied to clipboard!');
      })
      .catch(() => {
        toast.error(t('copy-failed') || 'Failed to copy the link.');
      });
  };

  const handleEmailInputChange = (value: string) => {
    setEmailInput(value);

    if (!value.trim()) {
      setEmailError(null);
      return;
    }

    const emails = parseEmails(value);
    const invalid = emails.filter(e => !isValidEmail(e));

    if (invalid.length > 0) {
      setEmailError(
        t('invalid-email-addresses', { emails: invalid.join(', ') }) ||
          `Invalid email(s): ${invalid.join(', ')}`
      );
    } else {
      setEmailError(null);
    }
  };

  const handleSendEmail = () => {
    if (!sessionId || !documentId) return;

    const emails = parsedEmails;
    if (!emails.length) {
      setEmailError(
        t('please-enter-at-least-one-email') ||
          'Please enter at least one email address.'
      );
      return;
    }

    const invalid = emails.filter(e => !isValidEmail(e));
    if (invalid.length > 0) {
      setEmailError(
        t('invalid-email-addresses', { emails: invalid.join(', ') }) ||
          `Invalid email(s): ${invalid.join(', ')}`
      );
      return;
    }

    setEmailError(null);

    sendMagicLinkEmail({
      sessionId,
      documentId,
      emails,
      message: message.trim() || undefined
    })
      .then(data => {
        setLastEmailsSentCount(data?.result?.emails_sent?.length ?? null);
        // Keep dialog open so user can send to more people, but clear inputs
        setLinkModal(false);
        setEmailInput('');
        setMessage('');
      })
      .catch(() => {
        // Error toast handled in hook
      });
  };

  return (
    <div className={cn('flex justify-end gap-1 items-center')}>
      {/* Burn-in progress banner */}
      {showBurnInBanner && (
        <div
          className="fixed bottom-4 right-4 z-50 flex items-start gap-3 rounded-2xl border px-4 py-3 shadow-lg max-w-sm"
          style={{
            backgroundColor: getColour('secondLayer'),
            borderColor: getColour('border')
          }}
        >
          <Loader2 className="h-5 w-5 animate-spin flex-shrink-0 mt-0.5 text-primary" />
          <div className="flex flex-col gap-1 min-w-0">
            <p
              className="text-sm font-medium"
              style={{ color: getColour('normalText') }}
            >
              {t('burn-in-progress-title')}
            </p>
            <p className="text-xs" style={{ color: getColour('inactiveText') }}>
              {burnInStatus === 'queued'
                ? t('burn-in-queued')
                : t('burn-in-processing')}
            </p>
            <button
              onClick={() => setBurnInBannerDismissed(true)}
              className="mt-1 text-xs underline text-left"
              style={{ color: getColour('inactiveText') }}
            >
              {t('burn-in-progress-dismiss')}
            </button>
          </div>
        </div>
      )}
      <div className="hidden md:flex items-center gap-1 flex-wrap">
        {/* Button cluster hidden below md (#2231); phone users reach
            Export via the editor header's overflow menu, which drives
            `downloadOpen` on this component. */}
        {hasFeature('translation') && (
          <Button
            size="sm"
            variant="secondary"
            icon={<Languages size={12} />}
            label={t('translate')}
            onClick={() => setModal('translate')}
            className="h-9 max-w-fit min-[1080px]:w-32 justify-center text-xs px-2!"
          />
        )}
        <Button
          size="sm"
          variant="secondary"
          icon={<Upload size={12} />}
          label={t('export')}
          onClick={() => setModal('download')}
          className="h-9 max-w-fit min-[1080px]:w-32 justify-center text-xs px-2!"
        />
        <Button
          size="sm"
          variant="secondary"
          icon={<WandSparkles size={12} />}
          label={t('magic-link')}
          onClick={handleGenerateLink}
          disabled={documentResult?.result?.finished || generatingLink}
          loading={generatingLink}
          className="h-9 max-w-fit min-[1080px]:w-32 justify-center text-xs px-2!"
        />
      </div>
      {/* PublishAction mounts outside the hidden-on-narrow wrapper so the
          dialog is still reachable from the overflow menu at phone widths
          (#2303). The button itself hides on < md via its own class. */}
      {sessionId && documentId && (
        <PublishAction
          sessionId={sessionId}
          documentId={documentId}
          defaultPayloadKind="captions"
          allowKindToggle
          fallbackLanguages={session?.language ? [session.language] : []}
          open={publishOpen}
          onOpenChange={onPublishOpenChange}
        />
      )}

      {/* Download modal */}
      <Modal
        isOpen={modal === 'download'}
        onClose={() => setModal('')}
        size="medium"
        title={t('export-subtitles')}
        caption={t('export-subtitles-description')}
        footerActions={[
          <Button
            onClick={() => setModal('')}
            label={t('cancel')}
            size="small"
            variant="smoke"
            key="cancel-download"
          />
        ]}
      >
        <div className="space-y-6">
          {/* Content Options Section */}
          <section className="px-4 py-2">
            <div className="flex flex-col gap-1">
              <div>
                <h3
                  className="text-sm font-semibold"
                  style={{ color: getColour('normalText') }}
                >
                  {t('content-options')}
                </h3>
              </div>
              <div className="flex items-center justify-between py-1">
                <Checkbox
                  id="include_speakers"
                  name="include_speakers"
                  label={t('include-speakers')}
                  checked={includeSpeakers}
                  onChange={e => setIncludeSpeakers(e.target.checked)}
                  variant="primary"
                  wrapperClass="w-full flex items-center justify-between cursor-pointer select-none"
                  labelClass="text-sm font-medium"
                />
              </div>
            </div>
          </section>

          {/* Export Options Section */}
          <section className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3
                className="text-sm font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {t('export-options')}
              </h3>
              <p
                className="text-xs"
                style={{ color: getColour('inactiveText') }}
              >
                {t('pick-subtitle-format')}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {formatOptions
                .filter(
                  option =>
                    (option.key !== 'media' && option.key !== 'mp4-burnt') ||
                    !!session?.media_url
                )
                // Burn-in is a plan feature (plan2.allow_burnt_export, surfaced
                // as features.burnt_export). Without this the option was offered
                // to every org and only failed at the API with a 402.
                .filter(
                  option =>
                    option.key !== 'mp4-burnt' || hasFeature('burnt_export')
                )
                .map(option => {
                  const isBurnInProcessing =
                    option.key === 'mp4-burnt' &&
                    (burnInStatus === 'queued' ||
                      burnInStatus === 'processing');

                  const IconComponent =
                    option.key === 'docx'
                      ? FileText
                      : option.key === 'html'
                        ? FileCode
                        : option.key === 'media' || option.key === 'mp4-burnt'
                          ? FileVideo
                          : Subtitles;

                  return (
                    <button
                      key={option.key}
                      type="button"
                      onClick={async () => {
                        setExporting(true);
                        await handleExport(option.key);
                        setExporting(false);
                      }}
                      disabled={exporting || isBurnInProcessing}
                      className="flex h-full w-full items-start gap-3 rounded-2xl border px-3 py-2.5 text-left text-sm shadow-sm hover:border-primary/40 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-60"
                      style={{
                        borderColor: getColour('border'),
                        backgroundColor: getColour('secondLayer')
                      }}
                    >
                      <span className="mt-0.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-2xl bg-primary/5 text-primary">
                        {isBurnInProcessing ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <IconComponent className="h-4 w-4" />
                        )}
                      </span>
                      <span className="flex flex-col gap-0.5">
                        <span
                          className="font-medium"
                          style={{ color: getColour('normalText') }}
                        >
                          {t(option.labelKey) || option.name}
                        </span>
                        <span
                          className="text-xs"
                          style={{ color: getColour('inactiveText') }}
                        >
                          {t(option.descriptionKey)}
                        </span>
                      </span>
                    </button>
                  );
                })}
            </div>
          </section>
        </div>
      </Modal>

      {/* Translate modal */}
      <Modal
        isOpen={modal === 'translate'}
        onClose={() => setModal('')}
        size="medium"
        customWidth="560px"
        title={t('translate-document')}
        caption={session?.filename || ''}
        footerActions={[
          <Button
            onClick={() => setModal('')}
            label={t('cancel')}
            size="small"
            variant="smoke"
            key="cancel-translate"
          />,
          <Button
            onClick={handleTranslate}
            label={t('translate')}
            size="small"
            variant="primary"
            loading={translationIsLoading}
            disabled={translationIsLoading}
            key="confirm-translate"
          />
        ]}
      >
        <div className="mb-4 flex gap-2 items-center text-sm">
          <label
            htmlFor="translationInput"
            className={cn(
              'block text-gray-700 dark:text-zinc-300 col-span-2 min-w-32',
              css(styles.userText)
            )}
          >
            {t('translation-name')}
          </label>
          <Input
            id="translationInput"
            placeholder={session?.filename}
            value={filenameInput}
            onChange={e => setFilenameInput(e.target.value)}
            className={cn('col-span-3')}
          />
        </div>
        <div className="mb-4 flex gap-2 items-center text-sm">
          <label
            htmlFor="languageSelect"
            className={cn(
              'block text-gray-700 dark:text-zinc-300 max-w-fit col-span-2 min-w-32',
              css(styles.userText)
            )}
          >
            {t('translate-to')}
          </label>
          <SelectInput
            id="languageSelect"
            options={options}
            value={selectedLanguages}
            name="type"
            onChange={(selectedOptions: MultiValue<Option>) => {
              setSelectedLanguages(selectedOptions as Option[]);
              setSelectedGlossaryId(null);
            }}
            className="min-w-full"
            isMulti
          />
        </div>

        {matchingGlossaries.length > 0 && (
          <div className="mb-4 flex gap-2 items-center text-sm">
            <label
              htmlFor="glossarySelect"
              className={cn(
                'block text-gray-700 dark:text-zinc-300 max-w-fit col-span-2 min-w-32',
                css(styles.userText)
              )}
            >
              {t('glossary')}
            </label>
            <SelectInput
              id="glossarySelect"
              value={
                selectedGlossaryId
                  ? {
                      label:
                        matchingGlossaries.find(
                          (g: Glossary) => g.id === selectedGlossaryId
                        )?.name || '',
                      value: selectedGlossaryId
                    }
                  : {
                      label: t('automatic'),
                      value: ''
                    }
              }
              name="glossary"
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onChange={(option: any) => {
                setSelectedGlossaryId(
                  option?.value ? Number(option.value) : null
                );
              }}
              className="min-w-full"
              options={[
                { label: t('automatic'), value: '' },
                ...matchingGlossaries.map((g: Glossary) => ({
                  label: `${g.name} (${g.source_language.toUpperCase()} → ${g.target_language.toUpperCase()})`,
                  value: g.id
                }))
              ]}
            />
          </div>
        )}

        {/* Translate to org default languages */}
        {orgDefaultLanguages.length > 0 && (
          <>
            <div
              className="my-3 border-t"
              style={{ borderColor: getColour('border') }}
            />
            <div className="flex items-center justify-between gap-2">
              <div>
                <p
                  className="text-xs font-medium"
                  style={{ color: getColour('normalText') }}
                >
                  {t('translate-to-org-languages')}
                </p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {orgDefaultLanguages.map(lang => (
                    <span
                      key={lang}
                      className="inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded-full uppercase"
                      style={{
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        color: '#3b82f6'
                      }}
                    >
                      {lang}
                    </span>
                  ))}
                </div>
              </div>
              <Button
                onClick={() => {
                  handleTranslateAll();
                  setModal('');
                }}
                label={t('translate-all')}
                size="small"
                variant="secondary"
                loading={translateAllLoading}
                disabled={translateAllLoading}
              />
            </div>
          </>
        )}
      </Modal>

      {/* Link + email share modal */}
      {linkModal && (
        <Modal
          isOpen={linkModal}
          onClose={() => setLinkModal(false)}
          size="medium"
          title={t('magic-link')}
          footerActions={[
            <Button
              onClick={() => setLinkModal(false)}
              label={t('close')}
              size="small"
              variant="smoke"
              key="close-button"
            />
          ]}
        >
          <div className="flex flex-col gap-4">
            {/* Magic link + copy */}
            <div className="flex items-center gap-2">
              <p
                className="break-all text-xs underline max-w-full"
                style={{ wordWrap: 'break-word' }}
              >
                {link}
              </p>
              <button
                onClick={handleCopyLink}
                className="p-2 rounded-2xl hover:bg-gray-200 dark:hover:bg-zinc-700 transition"
                aria-label="Copy link"
              >
                <ClipboardCopy width={16} />
              </button>
            </div>

            <div className="border-t border-gray-200 dark:border-zinc-700" />

            {/* Email share section – same behavior as TranscriptExportDocument */}
            <div className="flex flex-col gap-2">
              <h4 className="text-sm font-medium">
                {t('send-via-email') || 'Send via Email'}
              </h4>

              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-600 dark:text-zinc-400">
                  {t('email-addresses') ||
                    'Email addresses (comma or newline separated)'}
                </label>
                <textarea
                  value={emailInput}
                  onChange={e => handleEmailInputChange(e.target.value)}
                  rows={2}
                  placeholder={
                    t('enter-email-addresses') || 'Enter email addresses...'
                  }
                  className="w-full rounded-2xl border px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                  style={{
                    backgroundColor: getColour('secondLayer'),
                    borderColor: getColour('border'),
                    color: getColour('normalText')
                  }}
                />
                {emailError && (
                  <p className="text-xs text-red-500">{emailError}</p>
                )}
              </div>

              <div className="flex flex-col gap-1 mt-2">
                <label className="text-xs text-gray-600 dark:text-zinc-400">
                  {t('optional-message-label') ||
                    'Add an optional message for recipients (optional)'}
                </label>
                <textarea
                  value={message}
                  onChange={e =>
                    setMessage(e.target.value.slice(0, MAX_MESSAGE_LENGTH))
                  }
                  rows={3}
                  placeholder={
                    t('optional-message-placeholder') ||
                    'Add an optional message for recipients (optional)'
                  }
                  className="w-full rounded-2xl border px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                  style={{
                    backgroundColor: getColour('secondLayer'),
                    borderColor: getColour('border'),
                    color: getColour('normalText')
                  }}
                />
                <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-zinc-400">
                  <span>
                    {t('characters-remaining-label', 'Characters remaining')}:{' '}
                    {remainingChars}
                  </span>

                  {lastEmailsSentCount != null && lastEmailsSentCount > 0 && (
                    <span className="text-green-600">
                      {t('magic-link-sent-to-recipients')}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex justify-end mt-2">
                <Button
                  label={t('send-email') || 'Send Email'}
                  variant="primary"
                  size="small"
                  icon={<Mail className="-ml-0.5" width={16} />}
                  onClick={handleSendEmail}
                  loading={isSendingEmail}
                  disabled={
                    isSendingEmail || !parsedEmails.length || !!emailError
                  }
                />
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
